<template>
  <AppShell debug-title="/customers/me" :debug-data="customerDebugData">
    <section v-if="!wizardStarted" class="space-y-8">
      <div class="space-y-3">
        <p class="app-eyebrow">{{ t("onboarding.eyebrow") }}</p>
        <h1 class="text-3xl font-bold tracking-tight md:text-4xl">
          {{ t("onboarding.welcomeTitle") }}
        </h1>
        <p class="max-w-2xl text-sm text-brand-muted">
          {{ t("onboarding.welcomeDescription") }}
        </p>
      </div>

      <Card class="rounded-none border-brand-line bg-brand-surface shadow-none">
        <CardContent
          class="space-y-5 p-8 text-sm leading-7 text-brand-muted-strong"
        >
          <p>{{ t("onboarding.intro1") }}</p>
          <p>
            {{ t("onboarding.intro2") }}
          </p>
          <p>
            {{ t("onboarding.intro3") }}
          </p>
          <div
            class="flex items-center gap-3 border-t border-brand-line pt-5 text-brand-ink"
          >
            <Clock class="size-5 shrink-0" />
            <p class="text-sm font-semibold">{{ t("onboarding.duration") }}</p>
          </div>
        </CardContent>
      </Card>

      <Card class="rounded-none border-brand-line bg-brand-surface shadow-none">
        <CardHeader class="p-8 pb-4">
          <p class="app-eyebrow">{{ t("onboarding.kycStatus") }}</p>
          <CardTitle class="text-xl font-bold">{{
            t("onboarding.notStarted")
          }}</CardTitle>
        </CardHeader>
        <CardContent class="space-y-8 p-8 pt-2">
          <div class="grid gap-4 md:grid-cols-4">
            <Button
              class="h-12 rounded-none border-0 bg-brand-fill text-[11px] font-bold uppercase tracking-[0.14em] text-white shadow-none hover:bg-brand-fill-hover"
              @click="startWizard"
            >
              <ArrowRight class="size-4" />
              {{ t("onboarding.start") }}
            </Button>
          </div>
        </CardContent>
      </Card>
    </section>

    <section v-else class="space-y-12">
      <div class="space-y-10">
        <Stepper :model-value="stepIndex + 1" class="gap-0 flex w-full">
          <StepperItem
            v-for="(item, index) in steps"
            :key="item.id"
            class="relative flex-col items-start gap-0"
            :class="index < steps.length - 1 ? 'flex-1' : 'shrink-0'"
            :step="index + 1"
            :disabled="index > stepIndex"
          >
            <StepperTrigger
              class="items-start gap-4 p-0 text-left"
              :disabled="index > stepIndex"
            >
              <StepperIndicator
                class="size-9 rounded-full border-2 border-brand-ink bg-brand-surface text-sm font-bold text-brand-ink transition-colors group-data-[state=active]:bg-brand-fill group-data-[state=active]:text-white group-data-[state=completed]:bg-brand-fill group-data-[state=completed]:text-white"
              >
                <Check v-if="stepIndex > index" class="size-4" />
                <component :is="item.icon" v-else class="size-4" />
              </StepperIndicator>
              <div class="flex flex-col">
                <span
                  class="text-[10px] font-bold uppercase tracking-[0.18em] text-brand-label"
                  >{{ t("onboarding.stepLabel", { step: index + 1 }) }}</span
                >
                <StepperTitle class="text-sm font-bold text-brand-ink">{{
                  item.label
                }}</StepperTitle>
              </div>
              <StepperDescription class="sr-only">{{
                t("onboarding.stepDescription", {
                  step: index + 1,
                  total: steps.length,
                })
              }}</StepperDescription>
            </StepperTrigger>
            <StepperSeparator
              v-if="index < steps.length - 1"
              class="absolute left-9 right-0 top-4.5 h-0.5 w-auto bg-brand-line group-data-[state=completed]:bg-brand-fill"
            />
          </StepperItem>
        </Stepper>
      </div>

      <form class="space-y-10" @submit.prevent="submitCurrentStep">
        <PersonalStepForm
          v-if="currentStep === OnboardingStep.PERSONAL"
          :draft="draft"
          :errors="errors"
        />

        <div v-else-if="currentStep === OnboardingStep.PEP" class="space-y-8">
          <div class="space-y-3">
            <p class="app-eyebrow">{{ t("onboarding.pepEyebrow") }}</p>
            <h1 class="text-3xl font-bold tracking-tight">
              {{ t("onboarding.pepTitle") }}
            </h1>
            <p class="max-w-3xl text-sm leading-7 text-brand-muted-strong">
              {{ t("onboarding.pepDescription") }}
            </p>
          </div>

          <div class="space-y-3">
            <label
              class="flex cursor-pointer items-start gap-4 border border-brand-line bg-brand-surface p-5 text-sm leading-7 text-brand-muted-strong transition-colors hover:border-brand-ink/40"
            >
              <input
                v-model="draft.pep"
                class="mt-1 size-5 shrink-0 accent-brand-ink"
                type="checkbox"
              />
              <span
                ><strong class="text-brand-ink">{{
                  t("onboarding.pepYes")
                }}</strong
                >, {{ t("onboarding.pepText") }}</span
              >
            </label>
            <label
              class="flex cursor-pointer items-start gap-4 border border-brand-line bg-brand-surface p-5 text-sm leading-7 text-brand-muted-strong transition-colors hover:border-brand-ink/40"
            >
              <input
                v-model="draft.onOwnAccount"
                class="mt-1 size-5 shrink-0 accent-brand-ink"
                type="checkbox"
              />
              <span>{{ t("onboarding.ownAccountText") }}</span>
            </label>
          </div>
          <p
            v-if="errors.actsOnOwnAccount"
            class="flex items-center gap-2 text-sm font-bold text-destructive"
          >
            <AlertCircle class="size-4" />
            {{ errors.actsOnOwnAccount }}
          </p>
        </div>

        <div v-else-if="currentStep === OnboardingStep.CUSTODY" class="space-y-8">
          <div class="space-y-3">
            <p class="app-eyebrow">{{ t("onboarding.custodyEyebrow") }}</p>
            <h1 class="text-3xl font-bold tracking-tight">
              {{ t("onboarding.custodyTitle") }}
            </h1>
            <p class="max-w-3xl text-sm leading-7 text-brand-muted-strong">
              {{ t("onboarding.custodyDescription") }}
            </p>
          </div>
          <label
            class="flex cursor-pointer items-start gap-4 border border-brand-line bg-brand-surface p-5 text-sm leading-7 text-brand-muted-strong transition-colors hover:border-brand-ink/40"
          >
            <input
              v-model="draft.safekeeping"
              class="mt-1 size-5 shrink-0 accent-brand-ink"
              type="checkbox"
            />
            <span>{{ t("onboarding.custodyConfirm") }}</span>
          </label>
          <p
            v-if="errors.acceptsCustodyTerms"
            class="flex items-center gap-2 text-sm font-bold text-destructive"
          >
            <AlertCircle class="size-4" />
            {{ errors.acceptsCustodyTerms }}
          </p>
          <label
            class="flex cursor-pointer items-start gap-4 border border-brand-line bg-brand-surface p-5 text-sm leading-7 text-brand-muted-strong transition-colors hover:border-brand-ink/40"
          >
            <input
              v-model="draft.agb"
              class="mt-1 size-5 shrink-0 accent-brand-ink"
              type="checkbox"
            />
            <span>
              {{ t("onboarding.agbPrefix") }}
              <button
                type="button"
                class="hidden font-semibold text-brand-ink underline underline-offset-2 hover:text-brand-ink-hover"
                :disabled="agbDocumentLoading"
                @click.stop="openAgbDocument"
              >
                {{ t("onboarding.agbLink") }}
              </button>
              <a
                  class="font-semibold text-brand-ink underline underline-offset-2 hover:text-brand-ink-hover"
                  :href="t('footer.links.terms.href')"
                  target="_blank"
                  rel="noopener noreferrer"
              >{{ t("footer.links.terms.labelInline") }}</a>
              {{ t("onboarding.agbSuffix") }}
            </span>
          </label>
          <p
            v-if="errors.acceptsAgb"
            class="flex items-center gap-2 text-sm font-bold text-destructive"
          >
            <AlertCircle class="size-4" />
            {{ errors.acceptsAgb }}
          </p>
        </div>

        <div v-else class="space-y-8">
          <div class="space-y-3">
            <p class="app-eyebrow">
              {{ t("onboarding.identificationEyebrow") }}
            </p>
            <h1 class="text-3xl font-bold tracking-tight">
              {{ t("onboarding.identificationTitle") }}
            </h1>
            <p class="max-w-3xl text-sm leading-7 text-brand-muted-strong">
              {{ t("onboarding.identificationDescription") }}
            </p>
          </div>
          <div class="grid gap-5 md:grid-cols-3">
            <button
              v-for="method in kycMethods"
              :key="method.id"
              type="button"
              class="relative flex min-h-64 flex-col border p-6 text-left transition-all"
              :class="
                selectedKycMethod === method.id
                  ? 'border-brand-ink bg-brand-surface shadow-[0_0_0_1px_var(--brand-ink)]'
                  : 'border-brand-line bg-brand-surface hover:border-brand-ink/40'
              "
              @click="selectedKycMethod = method.id"
            >
              <div class="flex items-center justify-between">
                <component :is="method.icon" class="size-6 text-brand-ink" />
                <CheckCircle2
                  v-if="selectedKycMethod === method.id"
                  class="size-5 text-brand-ink"
                />
              </div>
              <div class="mt-4 text-lg font-bold">{{ method.title }}</div>
              <div
                v-if="method.subtitle"
                class="mt-1 text-xs font-semibold uppercase tracking-wide text-brand-muted"
              >
                {{ method.subtitle }}
              </div>
              <ul
                class="mt-4 space-y-2 text-sm leading-6 text-brand-muted-strong"
              >
                <li v-for="item in method.items" :key="item" class="flex gap-2">
                  <Check class="mt-1 size-3.5 shrink-0 text-brand-ink" />
                  <span>{{ item }}</span>
                </li>
              </ul>
            </button>
          </div>
          <div class="border-t border-brand-line pt-8">
            <h2 class="text-xl font-bold">
              {{ t("onboarding.identificationNeeds") }}
            </h2>
            <div class="mt-5 grid gap-4 md:grid-cols-2">
              <div
                class="flex items-start gap-3 border border-brand-line bg-brand-surface p-4"
              >
                <Landmark class="mt-0.5 size-5 shrink-0 text-brand-ink" />
                <div class="text-sm leading-6 text-brand-muted-strong">
                  <strong class="text-brand-ink">{{
                    t("onboarding.onlineBanking")
                  }}</strong>
                  &mdash; {{ t("onboarding.onlineBankingText") }}
                </div>
              </div>
              <div
                class="flex items-start gap-3 border border-brand-line bg-brand-surface p-4"
              >
                <Smartphone class="mt-0.5 size-5 shrink-0 text-brand-ink" />
                <div class="text-sm leading-6 text-brand-muted-strong">
                  <strong class="text-brand-ink">{{
                    t("onboarding.mobilePhone")
                  }}</strong>
                  &mdash; {{ t("onboarding.mobilePhoneText") }}
                </div>
              </div>
            </div>
            <p
              class="mt-5 flex items-start gap-2 text-sm leading-7 text-brand-muted-strong"
            >
              <Info class="mt-0.5 size-4 shrink-0 text-brand-ink" />
              <span
                ><strong>{{ t("onboarding.note") }}</strong>
                {{ t("onboarding.bankidentNote") }}</span
              >
            </p>
          </div>
        </div>

        <div
          class="grid gap-4 border-t border-brand-line pt-8 md:grid-cols-[auto_1fr] md:items-center md:justify-between"
        >
          <Button
            type="button"
            variant="outline"
            class="h-12 rounded-none border-brand-line bg-brand-surface text-[12px] font-bold uppercase tracking-[0.14em] text-brand-ink shadow-none hover:bg-brand-surface-muted disabled:opacity-50"
            :disabled="stepIndex === 0 || saving"
            @click="previousStep"
          >
            <ChevronLeft class="size-4" />
            {{ t("onboarding.back") }}
          </Button>
          <Button
            type="submit"
            class="h-12 rounded-none border-0 bg-brand-fill text-[12px] font-bold uppercase tracking-[0.14em] text-white shadow-none hover:bg-brand-fill-hover md:justify-self-end md:px-10"
            :disabled="saving"
          >
            <Loader2 v-if="saving" class="size-4 animate-spin" />
            {{ currentStep === OnboardingStep.KYC ? t("onboarding.identificationEyebrow") : t("onboarding.applyContinue") }}
            <ArrowRight v-if="!saving" class="size-4" />
          </Button>
        </div>
      </form>
    </section>
  </AppShell>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch, type Component } from "vue";
import { KycStartRequestDtoKycMethod, OnboardingDtoStatus, OnboardingStep, type CustomerDto } from "@token/shared";
import { toast } from "vue-sonner";
import {
  AlertCircle,
  ArrowRight,
  Banknote,
  Check,
  CheckCircle2,
  ChevronLeft,
  Clock,
  IdCard,
  Info,
  Landmark,
  Loader2,
  ShieldCheck,
  Smartphone,
  User,
  Video,
} from "lucide-vue-next";
import AppShell from "@/components/AppShell.vue";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useConfig } from "@/lib/config";
import { useCustomerSession } from "@/features/customer-session";
import { getDebugEnv } from "@/lib/debug-env";
import { useI18n } from "@/lib/i18n";
import {
  Stepper,
  StepperDescription,
  StepperIndicator,
  StepperItem,
  StepperSeparator,
  StepperTitle,
  StepperTrigger,
} from "@/components/ui/stepper";
import {
  createOnboardingApi,
  type KycOnboardingMethod,
  type OnboardingFormModel,
} from "@/features/onboarding";
import PersonalStepForm from "@/features/onboarding/PersonalStepForm.vue";
import { useRouter } from "vue-router";

const auth = useAuth();
const api = useApi();
const router = useRouter();
const config = useConfig();
const customerSession = useCustomerSession();
const onboardingApi = createOnboardingApi(api, {
  mode: config.apiUrl ? "http" : "mock",
});
const { t } = useI18n();

const agbDocumentLoading = ref(false);
const steps = computed<
  Array<{ id: OnboardingStep; label: string; icon: Component }>
>(() => [
  { id: OnboardingStep.PERSONAL, label: t("onboarding.step.personal"), icon: User },
  { id: OnboardingStep.PEP, label: t("onboarding.step.pep"), icon: ShieldCheck },
  { id: OnboardingStep.CUSTODY, label: t("onboarding.step.custody"), icon: Landmark },
  { id: OnboardingStep.KYC, label: t("onboarding.step.kyc"), icon: IdCard },
]);

const wizardStarted = ref(false);
const stepIndex = ref(0);
const saving = ref(false);
const customerDebugError = ref<string | null>(null);
const KYC_METHODS: readonly KycOnboardingMethod[] = [
  KycStartRequestDtoKycMethod.WEBID,
  KycStartRequestDtoKycMethod.WEBIDEID,
  KycStartRequestDtoKycMethod.WEBIDPAY,
  KycStartRequestDtoKycMethod.CSIDENT,
];
const selectedKycMethod = ref<KycOnboardingMethod | null>(config.kyc?.methods?.[0]?.method ?? KYC_METHODS[0] ?? null);
const errors = reactive<Record<string, string>>({});

async function openAgbDocument(): Promise<void> {
  if (agbDocumentLoading.value) return;
  agbDocumentLoading.value = true;
  try {
    const response = await api.fetch("/api/portal/documents/agb", {
      headers: { Accept: "*/*" },
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  } catch {
    toast.error(t("onboarding.documentLoadError"));
  } finally {
    agbDocumentLoading.value = false;
  }
}

const draft = reactive<OnboardingFormModel>({
  id: null,
  customerId: null,
  salutation: "Herr",
  titleBefore: "",
  titleAfter: "",
  firstName: auth.state.user?.name.split(" ")[0] ?? "",
  lastName: auth.state.user?.name.split(" ").slice(1).join(" ") ?? "",
  birthDate: "",
  nationality: "",
  birthPlace: "",
  streetName: "",
  houseNumber: "",
  postCode: "",
  city: "",
  country: "AT",
  mobileNumber: "",
  pep: false,
  safekeeping: false,
  agb: false,
  active: true,
  status: OnboardingDtoStatus.NOT_STARTED,
  onOwnAccount: false,
});

const currentStep = computed(
  () => steps.value[stepIndex.value]?.id ?? OnboardingStep.PERSONAL,
);
const customerDebugData = computed(() =>
  ({
    customer: customerDebugError.value ? undefined : customerSession.current,
    env: getDebugEnv(),
    ...(customerDebugError.value ? { error: customerDebugError.value } : {}),
  }),
);
const kycMethods = computed<
  Array<{
    id: KycOnboardingMethod;
    title: string;
    subtitle?: string;
    icon: Component;
    items: string[];
  }>
>(() => {
  const configuredMethods = config.kyc?.methods
    ?.filter((method): method is { method: KycOnboardingMethod; label?: string } => Boolean(method.method))
    .map((method) => ({
      id: method.method,
      title: method.label ?? kycMethodTitle(method.method),
      ...kycMethodPresentation(method.method),
    }));
  if (configuredMethods?.length) {
    return configuredMethods;
  }
  return KYC_METHODS.map((method) => ({
    id: method,
    title: kycMethodTitle(method),
    ...kycMethodPresentation(method),
  }));
});

watch(
  kycMethods,
  (methods) => {
    if (!methods.length) {
      selectedKycMethod.value = null;
      return;
    }
    if (!selectedKycMethod.value || !methods.some((method) => method.id === selectedKycMethod.value)) {
      selectedKycMethod.value = methods[0].id;
    }
  },
  { immediate: true },
);

onMounted(() => {
  customerSession
    .sync()
    .then(applyCustomerToDraft)
    .catch((error: unknown) => {
      customerDebugError.value =
        error instanceof Error
          ? error.message
          : t("onboarding.customerLoadError");
    });
});

async function startWizard() {
  await syncOnboardingId();
  wizardStarted.value = true;
}

function previousStep() {
  stepIndex.value = Math.max(0, stepIndex.value - 1);
}

async function submitCurrentStep() {
  if (!validateCurrentStep()) return;

  saving.value = true;
  try {
    const step = currentStep.value;
    await syncOnboardingId();
    applyStatusForStep(step);
    const savedDraft = await onboardingApi.saveStep(step, draft);
    Object.assign(draft, savedDraft);
    if (step === OnboardingStep.KYC) {
      if (!selectedKycMethod.value) {
        throw new Error(t("onboarding.saveError"));
      }
      const verification = await onboardingApi.startKyc(selectedKycMethod.value);
      await customerSession.sync({ force: true });
      toast.success(t("onboarding.saveSuccess"));
      if (verification.redirectUrl) {
        window.open(verification.redirectUrl, "_blank", "noopener,noreferrer");
      }
      await router.replace({ name: "kyc-status" });
      return;
    }
    stepIndex.value += 1;
  } catch (error) {
    toast.error(
      error instanceof Error ? error.message : t("onboarding.saveError"),
    );
  } finally {
    saving.value = false;
  }
}

function kycMethodPresentation(method: KycOnboardingMethod): {
  subtitle?: string;
  icon: Component;
  items: string[];
} {
  if (method === KycStartRequestDtoKycMethod.WEBIDPAY) {
    return {
      subtitle: t("onboarding.kyc.bankSubtitle"),
      icon: Banknote,
      items: [
        t("onboarding.kyc.bankItem1"),
        t("onboarding.kyc.bankItem2"),
        t("onboarding.kyc.bankItem3"),
      ],
    };
  }
  if (method === KycStartRequestDtoKycMethod.WEBID) {
    return {
      icon: Video,
      items: [
        t("onboarding.kyc.videoItem1"),
        t("onboarding.kyc.videoItem2"),
        t("onboarding.kyc.videoItem3"),
        "Sie geben eine TAN ein, um den Vorgang abzuschließen",
      ],
    };
  }
  return {
    icon: IdCard,
    items: [
      t("onboarding.kyc.eidItem1"),
      t("onboarding.kyc.eidItem2"),
      t("onboarding.kyc.eidItem3"),
    ],
  };
}

function kycMethodTitle(method: KycOnboardingMethod): string {
  if (method === KycStartRequestDtoKycMethod.WEBID) return "Webid VideoID";
  if (method === KycStartRequestDtoKycMethod.WEBIDEID) return "Webid EID";
  if (method === KycStartRequestDtoKycMethod.WEBIDPAY) return "Webid Bank-Ident";
  if (method === KycStartRequestDtoKycMethod.CSIDENT) return "Diverse EID Dienste (Signicat)";
  return method;
}

function applyStatusForStep(step: OnboardingStep) {
  if (step === OnboardingStep.PERSONAL && draft.status === OnboardingDtoStatus.NOT_STARTED) {
    draft.status = OnboardingDtoStatus.STARTED;
  }
  if (step === OnboardingStep.KYC) {
    draft.status = OnboardingDtoStatus.DONE;
  }
}

async function syncOnboardingId() {
  if (draft.id && draft.customerId) return;
  const customer = customerSession.current ?? (await customerSession.sync());
  const onboarding = customer.onboarding?.onboarding;
  draft.id = valueOrNull(onboarding?.id, draft.id);
  draft.customerId = valueOrNull(
    onboarding?.customerId,
    customer.id,
    draft.customerId,
  );
}

function applyCustomerToDraft(customer: CustomerDto) {
  const onboarding = customer.onboarding?.onboarding;
  draft.id = valueOrNull(onboarding?.id);
  draft.customerId = valueOrNull(onboarding?.customerId, customer.id);
  draft.salutation = salutationLabel(
    valueOrString(onboarding?.salutation, customer.salutation, draft.salutation),
  );
  draft.titleBefore = valueOrString(onboarding?.titleBefore, customer.titleBefore);
  draft.titleAfter = valueOrString(onboarding?.titleAfter, customer.titleAfter);
  draft.firstName = valueOrString(onboarding?.firstName, customer.firstName, draft.firstName);
  draft.lastName = valueOrString(onboarding?.lastName, customer.lastName, draft.lastName);
  draft.birthDate = valueOrString(onboarding?.birthDate, customer.birthDate);
  draft.nationality = valueOrString(onboarding?.nationality, customer.country, draft.nationality);
  draft.birthPlace = valueOrString(onboarding?.birthPlace, draft.birthPlace);
  draft.streetName = valueOrString(onboarding?.streetName, customer.streetName);
  draft.houseNumber = valueOrString(onboarding?.houseNumber, customer.houseNumber);
  draft.postCode = valueOrString(onboarding?.postCode, customer.postCode);
  draft.city = valueOrString(onboarding?.city, customer.city);
  draft.country = valueOrString(onboarding?.country, customer.country, draft.country);
  draft.mobileNumber = valueOrString(onboarding?.mobileNumber, customer.mobileNumber);
  draft.pep = Boolean(onboarding?.pep);
  draft.safekeeping = false;
  draft.agb = false;
  draft.status = onboarding?.status ?? draft.status;
}

function valueOrString(
  ...values: Array<string | number | boolean | null | undefined>
): string {
  const value = values.find((item) => item !== null && item !== undefined);
  return value === null || value === undefined ? "" : String(value);
}

function valueOrNull(
  ...values: Array<string | number | null | undefined>
): string | number | null {
  return values.find((item) => item !== null && item !== undefined) ?? null;
}

function salutationLabel(value: string): string {
  if (value === "M") return t("onboarding.salutation.mr");
  if (value === "F") return t("onboarding.salutation.ms");
  if (value === "D") return t("onboarding.salutation.diverse");
  return value;
}

function validateCurrentStep(): boolean {
  const next: Record<string, string> = {};
  const required = (key: string, value: string) => {
    if (!value.trim()) next[key] = t("onboarding.error.required");
  };
  const minLength = (key: string, value: string, min: number) => {
    if (!value.trim()) {
      next[key] = t("onboarding.error.required");
    } else if (value.trim().length < min) {
      next[key] = t("onboarding.error.minLength");
    }
  };

  if (currentStep.value === OnboardingStep.PERSONAL) {
    minLength("firstName", draft.firstName, 2);
    minLength("lastName", draft.lastName, 2);
    if (!draft.birthDate) {
      next.birthDate = t("onboarding.error.required");
    } else if (!isValidDate(draft.birthDate)) {
      next.birthDate = t("onboarding.error.invalidDate");
    } else if (!meetsMinAge(draft.birthDate, 18)) {
      next.birthDate = t("onboarding.error.minAge");
    }
    required("nationality", draft.nationality);
    required("birthPlace", draft.birthPlace);
    required("streetName", draft.streetName);
    required("houseNumber", draft.houseNumber);
    if (!draft.postCode) {
      next.postCode = t("onboarding.error.required");
    } else if (!/^\d{4,5}$/.test(draft.postCode.trim())) {
      next.postCode = t("onboarding.error.invalidPostCode");
    }
    required("city", draft.city);
    required("country", draft.country);
    if (!draft.mobileNumber) {
      next.mobileNumber = t("onboarding.error.required");
    } else if (!/^\+?[\d\s()-]{7,20}$/.test(draft.mobileNumber.trim())) {
      next.mobileNumber = t("onboarding.error.invalidPhone");
    }
  }
  if (currentStep.value === OnboardingStep.PEP && !draft.onOwnAccount) {
    next.actsOnOwnAccount = t("onboarding.error.ownAccount");
  }
  if (currentStep.value === OnboardingStep.CUSTODY && !draft.safekeeping) {
    next.acceptsCustodyTerms = t("onboarding.error.custody");
  }
  if (currentStep.value === OnboardingStep.CUSTODY && !draft.agb) {
    next.acceptsAgb = t("onboarding.error.agb");
  }

  // Altes errors-Objekt bereinigen und neu befüllen
  Object.keys(errors).forEach((k) => delete errors[k]);
  Object.assign(errors, next);

  return Object.keys(next).length === 0;
}

function isValidDate(value: string): boolean {
  const d = new Date(value);
  return !isNaN(d.getTime()) && value.length >= 10;
}

function meetsMinAge(birthDate: string, minAge: number): boolean {
  const birth = new Date(birthDate);
  const threshold = new Date();
  threshold.setFullYear(threshold.getFullYear() - minAge);
  return birth <= threshold;
}

</script>
